import React, { useState, Fragment } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, Vitals, StatDose, MaternityFile } from '../../types';
import { MaternityErrorBoundary } from '../../components/ui/MaternityErrorBoundary';
import { Activity, ArrowRight, History, Syringe, Trash2, Baby, Save, CheckCircle } from 'lucide-react';
interface Props {}
export const NursingDashboard: React.FC<Props> = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser,
    getPatientEncounters,
    maternityFiles,
    updateMaternityFile
  } = useData();
  const [activeTab, setActiveTab] = useState<'vitals' | 'medications' | 'maternity'>('vitals');
  const [selectedEncounter, setSelectedEncounter] = useState<Encounter | null>(null);
  const [selectedMaternityFileRaw, setSelectedMaternityFileRaw] = useState<MaternityFile | null>(null);
  // Older maternity files (and partially-saved drafts) may be missing the deeply
  // nested objects the form reads (`admission.date`, `presentPregnancy.hpTest`,
  // `delivery.deliverySummary`, etc). Reading into an undefined nested object
  // throws at render time and the entire dashboard goes blank. Normalize every
  // file before handing it to the form so every nested shape is present.
  const normalizeMaternityFile = (file: MaternityFile): MaternityFile => {
    // Treat both undefined and null (Firestore) as "missing".
    const f: any = file || {};
    const obj = (v: any) => v && typeof v === 'object' ? v : {};
    const arr = (v: any) => Array.isArray(v) ? v : [];
    return {
      ...f,
      patient: {
        firstName: '',
        surname: '',
        otherNames: '',
        age: '',
        religion: '',
        churchAttended: '',
        residentialAddress: '',
        locationLandmark: '',
        phone: '',
        ...obj(f.patient),
        nextOfKin: {
          name: '',
          phone: '',
          relationship: '',
          ...obj(obj(f.patient).nextOfKin)
        }
      } as any,
      spouse: {
        firstName: '',
        surname: '',
        otherNames: '',
        occupation: '',
        contact: '',
        ...obj(f.spouse)
      } as any,
      admission: {
        date: '',
        time: '',
        ...obj(f.admission)
      } as any,
      generalExam: {
        temp: '',
        pulse: '',
        respiration: '',
        bp: '',
        oedema: '',
        anemia: '',
        cardiovascularSystem: '',
        respiratorySystem: '',
        centralNervousSystem: '',
        breasts: '',
        varicosities: '',
        vaginalDischarge: '',
        otherAbnormalities: '',
        hb: '',
        abo: '',
        rh: '',
        ...obj(f.generalExam),
        msUrine: {
          sg: '',
          albumen: '',
          acetone: '',
          sugar: '',
          ...obj(obj(f.generalExam).msUrine)
        }
      } as any,
      presentPregnancy: {
        lmp: '',
        quickening: '',
        edd: '',
        gestationAge: '',
        ancSummaryFirstBp: '',
        ancSummaryAtWeeks: '',
        pelvicAssessment: '',
        abnormalities: '',
        ...obj(f.presentPregnancy),
        syphilisTest: {
          date: '',
          result: '',
          treatment: '',
          ...obj(obj(f.presentPregnancy).syphilisTest)
        },
        hpTest: {
          date: '',
          result: '',
          ...obj(obj(f.presentPregnancy).hpTest)
        }
      } as any,
      labourObservations: arr(f.labourObservations),
      delivery: {
        laceration: '',
        anterior: '',
        posterior: '',
        none: '',
        repaired: '',
        membranes: '',
        membranesDate: '',
        membranesTime: '',
        episiotomy: '',
        episiotomyRepaired: '',
        maternalARV: '',
        arvTypes: '',
        uterineMassage: '',
        motherOnHAART: '',
        cytotec: '',
        syntometrine: '',
        firstStageBeganDate: '',
        firstStageBeganTime: '',
        placentaDelivery: '',
        completeDilationDate: '',
        completeDilationTime: '',
        placentaDeliveredDate: '',
        placentaDeliveredTime: '',
        abnormalityAtDilation: '',
        abnormalityAtDilationDesc: '',
        postpartumHemorrhage: '',
        deliveryMethod: '',
        ebl: '',
        abnormalityAtDelivery: '',
        abnormalityAtDeliveryDesc: '',
        complications: '',
        ...obj(f.delivery),
        deliverySummary: arr(obj(f.delivery).deliverySummary)
      } as any,
      newbornEvaluation: {
        ega: '',
        alive: '',
        neonatalDeath: '',
        causeOfDeath: '',
        apgar1: '',
        apgar5: '',
        apgar10: '',
        generalExam: '',
        congenitalMalformation: '',
        congenitalMalformationDesc: '',
        infantARVs: '',
        infantFeeding: '',
        resuscitationMethods: '',
        crownHeelLength: '',
        headCircumference: '',
        skinJaundice: '',
        skinOther: '',
        fontanelles: '',
        eyes: '',
        eyesTreatment: '',
        mouth: '',
        neck: '',
        chest: '',
        abdomen: '',
        umbilicus: '',
        genitalia: '',
        urine: '',
        anus: '',
        meconium: '',
        upperLimbs: '',
        erbParalysis: '',
        lowerLimbs: '',
        ortolaniSign: '',
        back: '',
        reflexesGrasp: '',
        reflexesMoro: '',
        reflexesSucking: '',
        progressNotes: '',
        ...obj(f.newbornEvaluation)
      } as any,
      postDeliveryObservations: arr(f.postDeliveryObservations),
      puerperiumNotes: arr(f.puerperiumNotes)
    };
  };
  const selectedMaternityFile = selectedMaternityFileRaw;
  const setSelectedMaternityFile = (file: MaternityFile | null | ((prev: MaternityFile | null) => MaternityFile | null)) => {
    if (typeof file === 'function') {
      setSelectedMaternityFileRaw(prev => {
        const next = file(prev);
        return next ? normalizeMaternityFile(next) : null;
      });
    } else {
      setSelectedMaternityFileRaw(file ? normalizeMaternityFile(file) : null);
    }
  };
  const [vitals, setVitals] = useState({
    temp: '',
    pulse: '',
    resp: '',
    bp: '',
    sp02: '',
    weight: '',
    height: '',
    bmi: '',
    painScore: '',
    avpu: 'Alert'
  });
  // Medical alerts (Allergies + HIV) — captured at nursing assessment
  const [medicalAlerts, setMedicalAlerts] = useState<{
    allergies: string;
    hivStatus: 'Positive' | 'Negative' | 'Unknown';
  }>({
    allergies: '',
    hivStatus: 'Unknown'
  });
  // Stat Dose form state
  const [statDoseForm, setStatDoseForm] = useState({
    drugName: '',
    dose: '',
    route: 'PO',
    notes: ''
  });
  // Show patients in NURSING stage AND any active patient (so nurses can administer stat doses anytime)
  const pendingEncounters = encounters.filter(e => e.stage === 'NURSING');
  const activeEncounters = encounters.filter(e => e.stage !== 'COMPLETED' && e.stage !== 'NURSING');
  // Past completed encounters for the currently selected patient — drives the
  // "Vitals History" panel. If this is undefined the entire assessment screen
  // crashes at render time, hiding the vitals form.
  const pastEncounters = selectedEncounter ? encounters.filter(e => e.patientId === selectedEncounter.patientId && e.id !== selectedEncounter.id && e.vitals).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()) : [];
  const handleAddStatDose = () => {
    if (!selectedEncounter || !currentUser || !statDoseForm.drugName) return;
    const newDose: StatDose = {
      id: `stat-${Date.now()}`,
      drugName: statDoseForm.drugName,
      dose: statDoseForm.dose,
      route: statDoseForm.route,
      notes: statDoseForm.notes,
      givenAt: new Date().toISOString(),
      givenBy: currentUser.id,
      nurseName: currentUser.fullName
    };
    const existing = selectedEncounter.statDoses || [];
    updateEncounter(selectedEncounter.id, {
      statDoses: [...existing, newDose]
    });
    // Refresh selected encounter to show new dose
    setSelectedEncounter({
      ...selectedEncounter,
      statDoses: [...existing, newDose]
    });
    setStatDoseForm({
      drugName: '',
      dose: '',
      route: 'PO',
      notes: ''
    });
    alert('Stat dose recorded successfully.');
  };
  const handleSaveMaternityDraft = () => {
    if (!selectedMaternityFile) return;
    updateMaternityFile(selectedMaternityFile.id, selectedMaternityFile);
    alert('Maternity file draft saved.');
  };
  const handleCompleteMaternity = () => {
    if (!selectedMaternityFile || !currentUser) return;
    if (confirm('Are you sure you want to complete this maternity visit? It will be moved to the archive.')) {
      updateMaternityFile(selectedMaternityFile.id, {
        ...selectedMaternityFile,
        stage: 'COMPLETED',
        completedAt: new Date().toISOString(),
        completedBy: currentUser.id
      });
      alert('Maternity visit completed and archived.');
      setSelectedMaternityFile(null);
    }
  };
  const activeMaternityFiles = maternityFiles.filter(m => m.stage === 'NURSING');
  const calculateBMI = (weightStr: string, heightStr: string) => {
    const w = parseFloat(weightStr);
    const h = parseFloat(heightStr) / 100; // convert cm to m
    if (w > 0 && h > 0) {
      const bmi = (w / (h * h)).toFixed(1);
      setVitals(prev => ({
        ...prev,
        weight: weightStr,
        height: heightStr,
        bmi
      }));
    } else {
      setVitals(prev => ({
        ...prev,
        weight: weightStr,
        height: heightStr,
        bmi: ''
      }));
    }
  };
  const handleRemoveStatDose = (doseId: string) => {
    if (!selectedEncounter) return;
    const remaining = (selectedEncounter.statDoses || []).filter(d => d.id !== doseId);
    updateEncounter(selectedEncounter.id, {
      statDoses: remaining
    });
    setSelectedEncounter({
      ...selectedEncounter,
      statDoses: remaining
    });
  };
  const handleSaveVitals = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEncounter || !currentUser) return;
    const newVitals: Vitals = {
      ...vitals,
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id
    };
    updateEncounter(selectedEncounter.id, {
      vitals: newVitals,
      stage: 'CLINICIAN'
    });
    // Update patient's medical alerts
    const patientToUpdate = patients.find(p => p.id === selectedEncounter.patientId);
    if (patientToUpdate) {
      // In a real app we'd have a function like updatePatient(patientId, data)
      // Assuming it exists or we just update the encounter for now.
    }
    alert('Vitals saved and patient forwarded to Clinician.');
    setSelectedEncounter(null);
    setVitals({
      temp: '',
      pulse: '',
      resp: '',
      bp: '',
      sp02: '',
      weight: '',
      height: '',
      bmi: '',
      painScore: '',
      avpu: 'Alert'
    });
  };
  const patient = selectedEncounter ? patients.find(p => p.id === selectedEncounter.patientId) : null;
  return <div className="space-y-6" data-id="element-3427">
      <div className="flex justify-between items-center" data-id="element-3428">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-3429">Nursing Dashboard</h1>
        <div className="flex space-x-2" data-id="element-3430">
          <Badge variant="success" className="text-sm px-3 py-1" data-id="element-3431">
            {pendingEncounters.length} Vitals Pending
          </Badge>
          <Badge variant="info" className="text-sm px-3 py-1" data-id="element-3432">
            {activeEncounters.length} Active Patients
          </Badge>
          <Badge variant="pink" className="text-sm px-3 py-1 bg-pink-100 text-pink-800" data-id="element-3433">
            {activeMaternityFiles.length} Maternity
          </Badge>
        </div>
      </div>

      <div className="border-b border-gray-200" data-id="element-3434">
        <nav className="-mb-px flex space-x-8" data-id="element-3435">
          <button onClick={() => {
          setActiveTab('vitals');
          setSelectedEncounter(null);
          setSelectedMaternityFile(null);
        }} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'vitals' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-3436">
            Vitals Queue
          </button>
          <button onClick={() => {
          setActiveTab('medications');
          setSelectedEncounter(null);
          setSelectedMaternityFile(null);
        }} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'medications' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-3437">
            Medications & Treatments
          </button>
          <button onClick={() => {
          setActiveTab('maternity');
          setSelectedEncounter(null);
          setSelectedMaternityFile(null);
        }} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'maternity' ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-3438">
            <Baby className="w-4 h-4 inline-block mr-2 mb-1" data-id="element-3439" />
            Maternity Ward
          </button>
        </nav>
      </div>

      {activeTab === 'vitals' && <div className="space-y-6" data-id="element-3440">
          {!selectedEncounter ? <div className="space-y-4" data-id="element-3441">
              <Card title="Awaiting Vitals" noPadding data-id="element-3442">
                <ul className="divide-y divide-gray-200" data-id="element-3443">
                  {pendingEncounters.length > 0 ? pendingEncounters.map(enc => {
              const p = patients.find(pat => pat.id === enc.patientId);
              return <li key={enc.id} className="p-4 hover:bg-gray-50 cursor-pointer flex justify-between items-center" onClick={() => setSelectedEncounter(enc)} data-id="element-3444">
                          <div className="flex items-center" data-id="element-3445">
                            <div className="bg-blue-100 p-2 rounded-full mr-4" data-id="element-3446">
                              <Activity className="h-6 w-6 text-blue-600" data-id="element-3447" />
                            </div>
                            <div data-id="element-3448">
                              <p className="text-sm font-medium text-blue-600" data-id="element-3449">
                                {p?.bmcFileNumber || '—'}
                              </p>
                              <p className="text-lg font-bold text-gray-900" data-id="element-3450">
                                {p?.firstName || 'Patient'} {p?.lastName || ''}
                              </p>
                              <p className="text-sm text-gray-500" data-id="element-3451">
                                {p?.age ?? '—'} yrs • {p?.sex || '—'}
                              </p>
                            </div>
                          </div>
                          <ArrowRight className="h-5 w-5 text-gray-400" data-id="element-3452" />
                        </li>;
            }) : <li className="p-8 text-center text-gray-500" data-id="element-3453">
                      No patients waiting for vitals
                    </li>}
                </ul>
              </Card>
              <Card title="Active Patients — Record Additional Vitals" noPadding data-id="element-3454">
                <ul className="divide-y divide-gray-200" data-id="element-3455">
                  {activeEncounters.length > 0 ? activeEncounters.map(enc => {
              const p = patients.find(pat => pat.id === enc.patientId);
              return <li key={enc.id} className="p-4 hover:bg-gray-50 cursor-pointer flex justify-between items-center" onClick={() => setSelectedEncounter(enc)} data-id="element-3456">
                          <div className="flex items-center" data-id="element-3457">
                            <div className="bg-green-100 p-2 rounded-full mr-4" data-id="element-3458">
                              <Activity className="h-6 w-6 text-green-600" data-id="element-3459" />
                            </div>
                            <div data-id="element-3460">
                              <p className="text-sm font-medium text-blue-600" data-id="element-3461">
                                {p?.bmcFileNumber || '—'}
                              </p>
                              <p className="text-lg font-bold text-gray-900" data-id="element-3462">
                                {p?.firstName || 'Patient'} {p?.lastName || ''}
                              </p>
                              <p className="text-sm text-gray-500" data-id="element-3463">
                                {p?.age ?? '—'} yrs • {p?.sex || '—'} •{' '}
                                <span className="text-gray-400" data-id="element-3464">
                                  Stage: {enc.stage}
                                </span>
                              </p>
                            </div>
                          </div>
                          <ArrowRight className="h-5 w-5 text-gray-400" data-id="element-3465" />
                        </li>;
            }) : <li className="p-8 text-center text-gray-500" data-id="element-3466">
                      No active patients
                    </li>}
                </ul>
              </Card>
            </div> : <>
              <div className="flex justify-between items-center" data-id="element-3467">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-3468">
                  Nursing Assessment
                </h1>
                <Button variant="outline" onClick={() => setSelectedEncounter(null)} data-id="element-3469">
                  Back to Queue
                </Button>
              </div>

              <Card className="bg-blue-50 border-blue-200" data-id="element-3470">
                <div className="flex justify-between items-start" data-id="element-3471">
                  <div data-id="element-3472">
                    <h2 className="text-xl font-bold text-gray-900" data-id="element-3473">
                      {patient?.firstName || 'Patient'}{' '}
                      {patient?.lastName || ''}
                    </h2>
                    <p className="text-sm text-gray-600" data-id="element-3474">
                      BMC: {patient?.bmcFileNumber || '—'} •{' '}
                      {patient?.age ?? '—'} yrs • {patient?.sex || '—'}
                    </p>
                  </div>
                  <div className="text-right" data-id="element-3475">
                    <p className="text-sm font-medium text-red-600" data-id="element-3476">
                      Allergies: {medicalAlerts.allergies || '—'}
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-3477">
                      HIV Status: {medicalAlerts.hivStatus}
                    </p>
                  </div>
                </div>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3478">
                <div className="lg:col-span-2" data-id="element-3479">
                  <Card title="Enter Vitals" data-id="element-3480">
                    <form onSubmit={handleSaveVitals} className="space-y-6" data-id="element-3481">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-3482">
                        <Input label="Blood Pressure (mmHg)" placeholder="120/80" required value={vitals.bp} onChange={e => setVitals({
                    ...vitals,
                    bp: e.target.value
                  })} data-id="element-3483" />
                        <Input label="Temperature (°C)" type="number" step="0.1" required value={vitals.temp} onChange={e => setVitals({
                    ...vitals,
                    temp: e.target.value
                  })} data-id="element-3484" />
                        <Input label="Pulse (bpm)" type="number" required value={vitals.pulse} onChange={e => setVitals({
                    ...vitals,
                    pulse: e.target.value
                  })} data-id="element-3485" />
                        <Input label="Resp. Rate (cpm)" type="number" required value={vitals.resp} onChange={e => setVitals({
                    ...vitals,
                    resp: e.target.value
                  })} data-id="element-3486" />
                        <Input label="SpO2 (%)" type="number" required value={vitals.sp02} onChange={e => setVitals({
                    ...vitals,
                    sp02: e.target.value
                  })} data-id="element-3487" />
                        <Input label="Pain Score (0-10)" type="number" min="0" max="10" required value={vitals.painScore} onChange={e => setVitals({
                    ...vitals,
                    painScore: e.target.value
                  })} data-id="element-3488" />

                        <Input label="Weight (kg)" type="number" step="0.1" required value={vitals.weight} onChange={e => calculateBMI(e.target.value, vitals.height || '')} data-id="element-3489" />
                        <Input label="Height (cm)" type="number" step="0.1" required value={vitals.height} onChange={e => calculateBMI(vitals.weight || '', e.target.value)} data-id="element-3490" />
                        <Input label="BMI" readOnly value={vitals.bmi} className="bg-gray-50" data-id="element-3491" />
                      </div>

                      <div className="border-t border-gray-200 pt-4" data-id="element-3492">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3493">
                          AVPU Scale
                        </label>
                        <div className="flex space-x-4" data-id="element-3494">
                          {['Alert', 'Verbal', 'Pain', 'Unresponsive'].map(level => <label key={level} className="flex items-center" data-id="element-3495">
                                <input type="radio" name="avpu" value={level} checked={vitals.avpu === level} onChange={e => setVitals({
                        ...vitals,
                        avpu: e.target.value
                      })} className="mr-2" data-id="element-3496" />
                                {level}
                              </label>)}
                        </div>
                      </div>

                      {/* Medical Alerts (moved from Reception) */}
                      <div className="border-t border-gray-200 pt-4" data-id="element-3497">
                        <h4 className="text-md font-semibold text-gray-900 mb-3" data-id="element-3498">
                          Medical Alerts
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3499">
                          <Input label="Known Allergies" placeholder="e.g. Penicillin, Peanuts (or 'None')" required value={medicalAlerts.allergies} onChange={e => setMedicalAlerts({
                      ...medicalAlerts,
                      allergies: e.target.value
                    })} data-id="element-3500" />
                          <div data-id="element-3501">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3502">
                              HIV Status
                            </label>
                            <select required value={medicalAlerts.hivStatus} onChange={e => setMedicalAlerts({
                        ...medicalAlerts,
                        hivStatus: e.target.value as 'Positive' | 'Negative' | 'Unknown'
                      })} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-3503">
                              <option value="Unknown" data-id="element-3504">Unknown</option>
                              <option value="Positive" data-id="element-3505">Positive</option>
                              <option value="Negative" data-id="element-3506">Negative</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end pt-4" data-id="element-3507">
                        <Button type="submit" size="lg" data-id="element-3508">
                          Save & Forward to Clinician
                        </Button>
                      </div>
                    </form>
                  </Card>

                  {/* Stat / Start Dose Section */}
                  <Card title="Stat / Start Dose Administered" className="mt-6" action={<Syringe className="w-5 h-5 text-pink-500" data-id="element-3510" />} data-id="element-3509">
                    <p className="text-xs text-gray-500 mb-4" data-id="element-3511">
                      Record any medication given to the patient as a start/stat
                      dose. These will appear on the patient file and be visible
                      to the clinician.
                    </p>

                    {/* Existing Stat Doses */}
                    {selectedEncounter.statDoses && selectedEncounter.statDoses.length > 0 && <div className="mb-4 space-y-2" data-id="element-3512">
                          {selectedEncounter.statDoses.map(d => <div key={d.id} className="flex justify-between items-start bg-pink-50 border border-pink-200 rounded p-3 text-sm" data-id="element-3513">
                              <div data-id="element-3514">
                                <p className="font-bold text-gray-900" data-id="element-3515">
                                  {d.drugName}{' '}
                                  <span className="text-gray-600 font-normal" data-id="element-3516">
                                    {d.dose} • {d.route}
                                  </span>
                                </p>
                                <p className="text-xs text-gray-600 mt-0.5" data-id="element-3517">
                                  Given by {d.nurseName} •{' '}
                                  {new Date(d.givenAt).toLocaleString()}
                                </p>
                                {d.notes && <p className="text-xs text-gray-700 mt-1 italic" data-id="element-3518">
                                    {d.notes}
                                  </p>}
                              </div>
                              <button onClick={() => handleRemoveStatDose(d.id)} className="text-gray-400 hover:text-red-600 p-1" title="Remove" data-id="element-3519">
                                <Trash2 className="w-4 h-4" data-id="element-3520" />
                              </button>
                            </div>)}
                        </div>}

                    {/* Add New Stat Dose */}
                    <div className="grid grid-cols-2 gap-3 border-t border-gray-200 pt-4" data-id="element-3521">
                      <Input label="Drug Name" className="col-span-2" placeholder="e.g. Paracetamol" value={statDoseForm.drugName} onChange={e => setStatDoseForm({
                  ...statDoseForm,
                  drugName: e.target.value
                })} data-id="element-3522" />
                      <Input label="Dose" placeholder="e.g. 500mg" value={statDoseForm.dose} onChange={e => setStatDoseForm({
                  ...statDoseForm,
                  dose: e.target.value
                })} data-id="element-3523" />
                      <div data-id="element-3524">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3525">
                          Route
                        </label>
                        <select value={statDoseForm.route} onChange={e => setStatDoseForm({
                    ...statDoseForm,
                    route: e.target.value
                  })} className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-3526">
                          <option value="PO" data-id="element-3527">Oral (PO)</option>
                          <option value="IV" data-id="element-3528">Intravenous (IV)</option>
                          <option value="IM" data-id="element-3529">Intramuscular (IM)</option>
                          <option value="SC" data-id="element-3530">Subcutaneous (SC)</option>
                          <option value="PR" data-id="element-3531">Per Rectum (PR)</option>
                          <option value="Topical" data-id="element-3532">Topical</option>
                        </select>
                      </div>
                      <Input label="Notes (optional)" className="col-span-2" placeholder="Reason or observation..." value={statDoseForm.notes} onChange={e => setStatDoseForm({
                  ...statDoseForm,
                  notes: e.target.value
                })} data-id="element-3533" />
                    </div>
                    <div className="mt-4 flex justify-end" data-id="element-3534">
                      <Button type="button" onClick={handleAddStatDose} disabled={!statDoseForm.drugName} className="bg-pink-600 hover:bg-pink-700 text-white disabled:bg-gray-300" data-id="element-3535">
                        <Syringe className="w-4 h-4 mr-2" data-id="element-3536" /> Mark Stat Dose
                        Given
                      </Button>
                    </div>
                  </Card>
                </div>

                <div className="lg:col-span-1" data-id="element-3537">
                  <Card title="Vitals History" className="h-full" data-id="element-3538">
                    {pastEncounters.length > 0 ? <div className="space-y-4 overflow-y-auto max-h-[500px] pr-2" data-id="element-3539">
                        {pastEncounters.map(enc => <div key={enc.id} className="bg-gray-50 p-3 rounded border border-gray-200 text-sm" data-id="element-3540">
                            <p className="font-medium text-gray-900 mb-2 border-b pb-1" data-id="element-3541">
                              {new Date(enc.startedAt).toLocaleDateString()}
                            </p>
                            <div className="grid grid-cols-2 gap-2" data-id="element-3542">
                              <div data-id="element-3543">
                                <span className="text-gray-500" data-id="element-3544">BP:</span>{' '}
                                {enc.vitals?.bp}
                              </div>
                              <div data-id="element-3545">
                                <span className="text-gray-500" data-id="element-3546">Temp:</span>{' '}
                                {enc.vitals?.temp}°C
                              </div>
                              <div data-id="element-3547">
                                <span className="text-gray-500" data-id="element-3548">Pulse:</span>{' '}
                                {enc.vitals?.pulse}
                              </div>
                              <div data-id="element-3549">
                                <span className="text-gray-500" data-id="element-3550">SpO2:</span>{' '}
                                {enc.vitals?.sp02}%
                              </div>
                              <div data-id="element-3551">
                                <span className="text-gray-500" data-id="element-3552">Weight:</span>{' '}
                                {enc.vitals?.weight}kg
                              </div>
                              <div data-id="element-3553">
                                <span className="text-gray-500" data-id="element-3554">BMI:</span>{' '}
                                {enc.vitals?.bmi}
                              </div>
                            </div>
                          </div>)}
                      </div> : <div className="text-center py-8 text-gray-500 flex flex-col items-center" data-id="element-3555">
                        <History className="w-8 h-8 mb-2 text-gray-400" data-id="element-3556" />
                        <p data-id="element-3557">No previous vitals recorded</p>
                      </div>}
                  </Card>
                </div>
              </div>
            </>}
        </div>}

      {activeTab === 'maternity' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3558">
          <div className="lg:col-span-1" data-id="element-3559">
            <Card title="Active Maternity Files" noPadding data-id="element-3560">
              <ul className="divide-y divide-gray-200" data-id="element-3561">
                {activeMaternityFiles.length > 0 ? activeMaternityFiles.map(file => <li key={file.id} className={`p-4 hover:bg-gray-50 cursor-pointer ${selectedMaternityFile?.id === file.id ? 'bg-pink-50' : ''}`} onClick={() => setSelectedMaternityFile(file)} data-id="element-3562">
                      <div className="flex items-center" data-id="element-3563">
                        <div className="bg-pink-100 p-2 rounded-full mr-3" data-id="element-3564">
                          <Baby className="h-5 w-5 text-pink-600" data-id="element-3565" />
                        </div>
                        <div data-id="element-3566">
                          <p className="text-sm font-medium text-blue-600" data-id="element-3567">
                            {file.bmcFileNumber}
                          </p>
                          <p className="text-base font-bold text-gray-900" data-id="element-3568">
                            {file.patient.firstName} {file.patient.surname}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-3569">
                            Registered:{' '}
                            {new Date(file.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </li>) : <li className="p-8 text-center text-gray-500" data-id="element-3570">
                    <Baby className="mx-auto h-8 w-8 text-gray-300 mb-2" data-id="element-3571" />
                    <p data-id="element-3572">No active maternity patients</p>
                  </li>}
              </ul>
            </Card>
          </div>

          <div className="lg:col-span-2" data-id="element-3573">
            {selectedMaternityFile ? <MaternityErrorBoundary key={selectedMaternityFile.id} onReset={() => setSelectedMaternityFile(null)} data-id="element-3574">
                <div className="space-y-6" data-id="element-3575">
                  <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm border border-gray-200 sticky top-0 z-10" data-id="element-3576">
                    <div data-id="element-3577">
                      <h2 className="text-xl font-bold text-gray-900" data-id="element-3578">
                        {selectedMaternityFile.patient?.firstName}{' '}
                        {selectedMaternityFile.patient?.surname}
                      </h2>
                      <p className="text-sm text-gray-500" data-id="element-3579">
                        File: {selectedMaternityFile.bmcFileNumber} | Age:{' '}
                        {selectedMaternityFile.patient?.age}
                      </p>
                    </div>
                    <div className="flex space-x-2" data-id="element-3580">
                      <Button variant="outline" onClick={handleSaveMaternityDraft} data-id="element-3581">
                        <Save className="w-4 h-4 mr-2" data-id="element-3582" /> Save Draft
                      </Button>
                      <Button variant="primary" className="bg-pink-600 hover:bg-pink-700 text-white" onClick={handleCompleteMaternity} data-id="element-3583">
                        <CheckCircle className="w-4 h-4 mr-2" data-id="element-3584" /> Complete Visit
                      </Button>
                    </div>
                  </div>

                  <Card title="Admission & General Exam" data-id="element-3585">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-3586">
                      <Input type="date" label="Date of Admission" value={selectedMaternityFile.admission.date} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  admission: {
                    ...selectedMaternityFile.admission,
                    date: e.target.value
                  }
                })} data-id="element-3587" />
                      <Input type="time" label="Time of Admission" value={selectedMaternityFile.admission.time} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  admission: {
                    ...selectedMaternityFile.admission,
                    time: e.target.value
                  }
                })} data-id="element-3588" />
                    </div>
                    <h4 className="font-medium text-gray-900 mb-3 border-b pb-1" data-id="element-3589">
                      General Examination
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3590">
                      <Input label="Temp" value={selectedMaternityFile.generalExam.temp} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    temp: e.target.value
                  }
                })} data-id="element-3591" />
                      <Input label="Pulse" value={selectedMaternityFile.generalExam.pulse} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    pulse: e.target.value
                  }
                })} data-id="element-3592" />
                      <Input label="Respiration" value={selectedMaternityFile.generalExam.respiration} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    respiration: e.target.value
                  }
                })} data-id="element-3593" />
                      <Input label="BP" value={selectedMaternityFile.generalExam.bp} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    bp: e.target.value
                  }
                })} data-id="element-3594" />
                      <Input label="Oedema" value={selectedMaternityFile.generalExam.oedema} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    oedema: e.target.value
                  }
                })} data-id="element-3595" />
                      <Input label="Anemia" value={selectedMaternityFile.generalExam.anemia} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    anemia: e.target.value
                  }
                })} data-id="element-3596" />
                      <Input label="Hb%" value={selectedMaternityFile.generalExam.hb} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    hb: e.target.value
                  }
                })} data-id="element-3597" />
                      <Input label="ABO" value={selectedMaternityFile.generalExam.abo} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    abo: e.target.value
                  }
                })} data-id="element-3598" />
                      <Input label="Rh" value={selectedMaternityFile.generalExam.rh} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    rh: e.target.value
                  }
                })} data-id="element-3599" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-3600">
                      <Input label="Cardiovascular System" value={selectedMaternityFile.generalExam.cardiovascularSystem} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    cardiovascularSystem: e.target.value
                  }
                })} data-id="element-3601" />
                      <Input label="Respiratory System" value={selectedMaternityFile.generalExam.respiratorySystem} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    respiratorySystem: e.target.value
                  }
                })} data-id="element-3602" />
                      <Input label="Central Nervous System" value={selectedMaternityFile.generalExam.centralNervousSystem} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    centralNervousSystem: e.target.value
                  }
                })} data-id="element-3603" />
                      <Input label="Breasts" value={selectedMaternityFile.generalExam.breasts} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    breasts: e.target.value
                  }
                })} data-id="element-3604" />
                      <Input label="Varicosities" value={selectedMaternityFile.generalExam.varicosities} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    varicosities: e.target.value
                  }
                })} data-id="element-3605" />
                      <Input label="Vaginal Discharge" value={selectedMaternityFile.generalExam.vaginalDischarge} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    vaginalDischarge: e.target.value
                  }
                })} data-id="element-3606" />
                    </div>
                    <div className="mt-4" data-id="element-3607">
                      <Input label="Other Abnormalities" value={selectedMaternityFile.generalExam.otherAbnormalities} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    otherAbnormalities: e.target.value
                  }
                })} data-id="element-3608" />
                    </div>
                    <h4 className="font-medium text-gray-900 mt-6 mb-3 border-b pb-1" data-id="element-3609">
                      MS Urine
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-3610">
                      <Input label="SG" value={selectedMaternityFile.generalExam.msUrine.sg} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    msUrine: {
                      ...selectedMaternityFile.generalExam.msUrine,
                      sg: e.target.value
                    }
                  }
                })} data-id="element-3611" />
                      <Input label="Albumen" value={selectedMaternityFile.generalExam.msUrine.albumen} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    msUrine: {
                      ...selectedMaternityFile.generalExam.msUrine,
                      albumen: e.target.value
                    }
                  }
                })} data-id="element-3612" />
                      <Input label="Acetone" value={selectedMaternityFile.generalExam.msUrine.acetone} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    msUrine: {
                      ...selectedMaternityFile.generalExam.msUrine,
                      acetone: e.target.value
                    }
                  }
                })} data-id="element-3613" />
                      <Input label="Sugar" value={selectedMaternityFile.generalExam.msUrine.sugar} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  generalExam: {
                    ...selectedMaternityFile.generalExam,
                    msUrine: {
                      ...selectedMaternityFile.generalExam.msUrine,
                      sugar: e.target.value
                    }
                  }
                })} data-id="element-3614" />
                    </div>
                  </Card>

                  <Card title="Present Pregnancy" data-id="element-3615">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4" data-id="element-3616">
                      <Input type="date" label="LMP" value={selectedMaternityFile.presentPregnancy.lmp} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    lmp: e.target.value
                  }
                })} data-id="element-3617" />
                      <Input label="Quickening" value={selectedMaternityFile.presentPregnancy.quickening} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    quickening: e.target.value
                  }
                })} data-id="element-3618" />
                      <Input type="date" label="EDD" value={selectedMaternityFile.presentPregnancy.edd} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    edd: e.target.value
                  }
                })} data-id="element-3619" />
                      <Input label="Gestation Age (Weeks)" value={selectedMaternityFile.presentPregnancy.gestationAge} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    gestationAge: e.target.value
                  }
                })} data-id="element-3620" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-3621">
                      <Input label="Summary of ANC Findings 1st BP" value={selectedMaternityFile.presentPregnancy.ancSummaryFirstBp} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    ancSummaryFirstBp: e.target.value
                  }
                })} data-id="element-3622" />
                      <Input label="At Weeks" value={selectedMaternityFile.presentPregnancy.ancSummaryAtWeeks} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    ancSummaryAtWeeks: e.target.value
                  }
                })} data-id="element-3623" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-3624">
                      <Input type="date" label="Syphilis Test Date" value={selectedMaternityFile.presentPregnancy.syphilisTest.date} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    syphilisTest: {
                      ...selectedMaternityFile.presentPregnancy.syphilisTest,
                      date: e.target.value
                    }
                  }
                })} data-id="element-3625" />
                      <Input label="Result" value={selectedMaternityFile.presentPregnancy.syphilisTest.result} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    syphilisTest: {
                      ...selectedMaternityFile.presentPregnancy.syphilisTest,
                      result: e.target.value
                    }
                  }
                })} data-id="element-3626" />
                      <Input label="Treatment" value={selectedMaternityFile.presentPregnancy.syphilisTest.treatment} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    syphilisTest: {
                      ...selectedMaternityFile.presentPregnancy.syphilisTest,
                      treatment: e.target.value
                    }
                  }
                })} data-id="element-3627" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-3628">
                      <Input type="date" label="HP Test Date" value={selectedMaternityFile.presentPregnancy.hpTest.date} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    hpTest: {
                      ...selectedMaternityFile.presentPregnancy.hpTest,
                      date: e.target.value
                    }
                  }
                })} data-id="element-3629" />
                      <Input label="Result" value={selectedMaternityFile.presentPregnancy.hpTest.result} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    hpTest: {
                      ...selectedMaternityFile.presentPregnancy.hpTest,
                      result: e.target.value
                    }
                  }
                })} data-id="element-3630" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3631">
                      <Input label="Pelvic Assessment" value={selectedMaternityFile.presentPregnancy.pelvicAssessment} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    pelvicAssessment: e.target.value
                  }
                })} data-id="element-3632" />
                      <Input label="Abnormalities" value={selectedMaternityFile.presentPregnancy.abnormalities} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  presentPregnancy: {
                    ...selectedMaternityFile.presentPregnancy,
                    abnormalities: e.target.value
                  }
                })} data-id="element-3633" />
                    </div>
                  </Card>

                  <Card title="Observations for Latent Phase Labour" data-id="element-3634">
                    <div className="overflow-x-auto" data-id="element-3635">
                      <table className="w-full text-sm text-left" data-id="element-3636">
                        <thead className="bg-gray-50" data-id="element-3637">
                          <tr data-id="element-3638">
                            <th className="p-2 border" data-id="element-3639">Date</th>
                            <th className="p-2 border" data-id="element-3640">Time</th>
                            <th className="p-2 border" data-id="element-3641">Pulse</th>
                            <th className="p-2 border" data-id="element-3642">BP</th>
                            <th className="p-2 border" data-id="element-3643">FH</th>
                            <th className="p-2 border" data-id="element-3644">Temp</th>
                            <th className="p-2 border" data-id="element-3645">Urinalysis</th>
                            <th className="p-2 border" data-id="element-3646">Contractions</th>
                            <th className="p-2 border" data-id="element-3647">Cervical Dilation</th>
                            <th className="p-2 border" data-id="element-3648">Membranes Liquor</th>
                            <th className="p-2 border" data-id="element-3649">Remarks</th>
                            <th className="p-2 border" data-id="element-3650">Sign</th>
                            <th className="p-2 border" data-id="element-3651"></th>
                          </tr>
                        </thead>
                        <tbody data-id="element-3652">
                          {selectedMaternityFile.labourObservations.map((obs, index) => <tr key={obs.id} data-id="element-3653">
                                <td className="p-1 border" data-id="element-3654">
                                  <input className="w-full p-1 border rounded" value={obs.date} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].date = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-3655" />
                                </td>
                                <td className="p-1 border" data-id="element-3656">
                                  <input className="w-full p-1 border rounded" value={obs.time} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].time = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-3657" />
                                </td>
                                <td className="p-1 border" data-id="element-3658">
                                  <input className="w-full p-1 border rounded" value={obs.pulse} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].pulse = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-3659" />
                                </td>
                                <td className="p-1 border" data-id="element-3660">
                                  <input className="w-full p-1 border rounded" value={obs.bp} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].bp = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-3661" />
                                </td>
                                <td className="p-1 border" data-id="element-3662">
                                  <input className="w-full p-1 border rounded" value={obs.fh} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].fh = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-3663" />
                                </td>
                                <td className="p-1 border" data-id="element-3664">
                                  <input className="w-full p-1 border rounded" value={obs.temp} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].temp = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-3665" />
                                </td>
                                <td className="p-1 border" data-id="element-3666">
                                  <input className="w-full p-1 border rounded" value={obs.urinalysis} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].urinalysis = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-3667" />
                                </td>
                                <td className="p-1 border" data-id="element-3668">
                                  <input className="w-full p-1 border rounded" value={obs.contractions} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].contractions = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-3669" />
                                </td>
                                <td className="p-1 border" data-id="element-3670">
                                  <input className="w-full p-1 border rounded" value={obs.cervicalDilation} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].cervicalDilation = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-3671" />
                                </td>
                                <td className="p-1 border" data-id="element-3672">
                                  <input className="w-full p-1 border rounded" value={obs.membranesLiquor} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].membranesLiquor = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-3673" />
                                </td>
                                <td className="p-1 border" data-id="element-3674">
                                  <input className="w-full p-1 border rounded" value={obs.remarks} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].remarks = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-3675" />
                                </td>
                                <td className="p-1 border" data-id="element-3676">
                                  <input className="w-full p-1 border rounded" value={obs.sign} onChange={e => {
                          const newObs = [...selectedMaternityFile.labourObservations];
                          newObs[index].sign = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} data-id="element-3677" />
                                </td>
                                <td className="p-1 border text-center" data-id="element-3678">
                                  <button onClick={() => {
                          const newObs = selectedMaternityFile.labourObservations.filter((_, i) => i !== index);
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            labourObservations: newObs
                          });
                        }} className="text-red-500 hover:text-red-700" data-id="element-3679">
                                    <Trash2 className="w-4 h-4" data-id="element-3680" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => setSelectedMaternityFile({
                ...selectedMaternityFile,
                labourObservations: [...selectedMaternityFile.labourObservations, {
                  id: Date.now().toString(),
                  date: '',
                  time: '',
                  pulse: '',
                  bp: '',
                  fh: '',
                  temp: '',
                  urinalysis: '',
                  contractions: '',
                  cervicalDilation: '',
                  membranesLiquor: '',
                  remarks: '',
                  sign: ''
                }]
              })} data-id="element-3681">
                      + Add Observation
                    </Button>
                  </Card>

                  <Card title="Description of Labour and Delivery" data-id="element-3682">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-3683">
                      <Input label="Laceration" value={selectedMaternityFile.delivery.laceration} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    laceration: e.target.value
                  }
                })} data-id="element-3684" />
                      <Input label="Anterior" value={selectedMaternityFile.delivery.anterior} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    anterior: e.target.value
                  }
                })} data-id="element-3685" />
                      <Input label="Posterior" value={selectedMaternityFile.delivery.posterior} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    posterior: e.target.value
                  }
                })} data-id="element-3686" />
                      <Input label="None" value={selectedMaternityFile.delivery.none} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    none: e.target.value
                  }
                })} data-id="element-3687" />
                      <Input label="Repaired" value={selectedMaternityFile.delivery.repaired} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    repaired: e.target.value
                  }
                })} data-id="element-3688" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-3689">
                      <Input label="Membranes" value={selectedMaternityFile.delivery.membranes} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    membranes: e.target.value
                  }
                })} data-id="element-3690" />
                      <Input type="date" label="Date" value={selectedMaternityFile.delivery.membranesDate} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    membranesDate: e.target.value
                  }
                })} data-id="element-3691" />
                      <Input type="time" label="Time" value={selectedMaternityFile.delivery.membranesTime} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    membranesTime: e.target.value
                  }
                })} data-id="element-3692" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-3693">
                      <Input label="Episiotomy" value={selectedMaternityFile.delivery.episiotomy} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    episiotomy: e.target.value
                  }
                })} data-id="element-3694" />
                      <Input label="Repaired" value={selectedMaternityFile.delivery.episiotomyRepaired} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    episiotomyRepaired: e.target.value
                  }
                })} data-id="element-3695" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-3696">
                      <Input label="Maternal ARV" value={selectedMaternityFile.delivery.maternalARV} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    maternalARV: e.target.value
                  }
                })} data-id="element-3697" />
                      <Input label="Which ones (NPV, AZT, Other)" value={selectedMaternityFile.delivery.arvTypes} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    arvTypes: e.target.value
                  }
                })} data-id="element-3698" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4" data-id="element-3699">
                      <Input label="Uterine Massage" value={selectedMaternityFile.delivery.uterineMassage} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    uterineMassage: e.target.value
                  }
                })} data-id="element-3700" />
                      <Input label="Mother Already on HAART" value={selectedMaternityFile.delivery.motherOnHAART} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    motherOnHAART: e.target.value
                  }
                })} data-id="element-3701" />
                      <Input label="Cytotec" value={selectedMaternityFile.delivery.cytotec} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    cytotec: e.target.value
                  }
                })} data-id="element-3702" />
                      <Input label="Syntometrine" value={selectedMaternityFile.delivery.syntometrine} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    syntometrine: e.target.value
                  }
                })} data-id="element-3703" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-3704">
                      <Input type="date" label="1st Stage Began Date" value={selectedMaternityFile.delivery.firstStageBeganDate} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    firstStageBeganDate: e.target.value
                  }
                })} data-id="element-3705" />
                      <Input type="time" label="Time" value={selectedMaternityFile.delivery.firstStageBeganTime} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    firstStageBeganTime: e.target.value
                  }
                })} data-id="element-3706" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-3707">
                      <Input label="Placenta Delivery" value={selectedMaternityFile.delivery.placentaDelivery} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    placentaDelivery: e.target.value
                  }
                })} data-id="element-3708" />
                      <Input type="date" label="Delivered Date" value={selectedMaternityFile.delivery.placentaDeliveredDate} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    placentaDeliveredDate: e.target.value
                  }
                })} data-id="element-3709" />
                      <Input type="time" label="Time" value={selectedMaternityFile.delivery.placentaDeliveredTime} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    placentaDeliveredTime: e.target.value
                  }
                })} data-id="element-3710" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-3711">
                      <Input type="date" label="Complete Dilation Date" value={selectedMaternityFile.delivery.completeDilationDate} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    completeDilationDate: e.target.value
                  }
                })} data-id="element-3712" />
                      <Input type="time" label="Time" value={selectedMaternityFile.delivery.completeDilationTime} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    completeDilationTime: e.target.value
                  }
                })} data-id="element-3713" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-3714">
                      <Input label="Abnormality at Dilation" value={selectedMaternityFile.delivery.abnormalityAtDilation} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    abnormalityAtDilation: e.target.value
                  }
                })} data-id="element-3715" />
                      <Input label="Describe" value={selectedMaternityFile.delivery.abnormalityAtDilationDesc} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    abnormalityAtDilationDesc: e.target.value
                  }
                })} data-id="element-3716" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-3717">
                      <Input label="Postpartum Hemorrhage" value={selectedMaternityFile.delivery.postpartumHemorrhage} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    postpartumHemorrhage: e.target.value
                  }
                })} data-id="element-3718" />
                      <Input label="Delivery Method" value={selectedMaternityFile.delivery.deliveryMethod} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    deliveryMethod: e.target.value
                  }
                })} data-id="element-3719" />
                      <Input label="EBL (cc)" value={selectedMaternityFile.delivery.ebl} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    ebl: e.target.value
                  }
                })} data-id="element-3720" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-3721">
                      <Input label="Abnormality at Delivery" value={selectedMaternityFile.delivery.abnormalityAtDelivery} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    abnormalityAtDelivery: e.target.value
                  }
                })} data-id="element-3722" />
                      <Input label="Describe" value={selectedMaternityFile.delivery.abnormalityAtDeliveryDesc} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    abnormalityAtDeliveryDesc: e.target.value
                  }
                })} data-id="element-3723" />
                      <Input label="Complications" value={selectedMaternityFile.delivery.complications} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  delivery: {
                    ...selectedMaternityFile.delivery,
                    complications: e.target.value
                  }
                })} data-id="element-3724" />
                    </div>
                    <h4 className="font-medium text-gray-900 mt-6 mb-3 border-b pb-1" data-id="element-3725">
                      Delivery Summary
                    </h4>
                    <div className="overflow-x-auto" data-id="element-3726">
                      <table className="w-full text-sm text-left" data-id="element-3727">
                        <thead className="bg-gray-50" data-id="element-3728">
                          <tr data-id="element-3729">
                            <th className="p-2 border" data-id="element-3730">Date</th>
                            <th className="p-2 border" data-id="element-3731">Time of Birth</th>
                            <th className="p-2 border" data-id="element-3732">Sex</th>
                            <th className="p-2 border" data-id="element-3733">Weight</th>
                            <th className="p-2 border" data-id="element-3734">Delivered By</th>
                            <th className="p-2 border" data-id="element-3735"></th>
                          </tr>
                        </thead>
                        <tbody data-id="element-3736">
                          {selectedMaternityFile.delivery.deliverySummary.map((sum, index) => <tr key={sum.id} data-id="element-3737">
                                <td className="p-1 border" data-id="element-3738">
                                  <Input type="date" value={sum.date} onChange={e => {
                          const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                          newSum[index].date = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            delivery: {
                              ...selectedMaternityFile.delivery,
                              deliverySummary: newSum
                            }
                          });
                        }} data-id="element-3739" />
                                </td>
                                <td className="p-1 border" data-id="element-3740">
                                  <Input type="time" value={sum.timeOfBirth} onChange={e => {
                          const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                          newSum[index].timeOfBirth = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            delivery: {
                              ...selectedMaternityFile.delivery,
                              deliverySummary: newSum
                            }
                          });
                        }} data-id="element-3741" />
                                </td>
                                <td className="p-1 border" data-id="element-3742">
                                  <Input value={sum.sex} onChange={e => {
                          const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                          newSum[index].sex = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            delivery: {
                              ...selectedMaternityFile.delivery,
                              deliverySummary: newSum
                            }
                          });
                        }} data-id="element-3743" />
                                </td>
                                <td className="p-1 border" data-id="element-3744">
                                  <Input value={sum.weight} onChange={e => {
                          const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                          newSum[index].weight = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            delivery: {
                              ...selectedMaternityFile.delivery,
                              deliverySummary: newSum
                            }
                          });
                        }} data-id="element-3745" />
                                </td>
                                <td className="p-1 border" data-id="element-3746">
                                  <Input value={sum.deliveredBy} onChange={e => {
                          const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                          newSum[index].deliveredBy = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            delivery: {
                              ...selectedMaternityFile.delivery,
                              deliverySummary: newSum
                            }
                          });
                        }} data-id="element-3747" />
                                </td>
                                <td className="p-1 border text-center" data-id="element-3748">
                                  <button onClick={() => {
                          const newSum = selectedMaternityFile.delivery.deliverySummary.filter((_, i) => i !== index);
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            delivery: {
                              ...selectedMaternityFile.delivery,
                              deliverySummary: newSum
                            }
                          });
                        }} className="text-red-500 hover:text-red-700" data-id="element-3749">
                                    <Trash2 className="w-4 h-4" data-id="element-3750" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => setSelectedMaternityFile({
                ...selectedMaternityFile,
                delivery: {
                  ...selectedMaternityFile.delivery,
                  deliverySummary: [...selectedMaternityFile.delivery.deliverySummary, {
                    id: Date.now().toString(),
                    date: '',
                    timeOfBirth: '',
                    sex: '',
                    weight: '',
                    deliveredBy: ''
                  }]
                }
              })} data-id="element-3751">
                      + Add Baby
                    </Button>
                  </Card>

                  <Card title="Newborn Evaluation" data-id="element-3752">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4" data-id="element-3753">
                      <Input label="EGA" value={selectedMaternityFile.newbornEvaluation.ega} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    ega: e.target.value
                  }
                })} data-id="element-3754" />
                      <Input label="Alive" value={selectedMaternityFile.newbornEvaluation.alive} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    alive: e.target.value
                  }
                })} data-id="element-3755" />
                      <Input label="Neonatal Death" value={selectedMaternityFile.newbornEvaluation.neonatalDeath} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    neonatalDeath: e.target.value
                  }
                })} data-id="element-3756" />
                      <Input label="Cause of Death" value={selectedMaternityFile.newbornEvaluation.causeOfDeath} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    causeOfDeath: e.target.value
                  }
                })} data-id="element-3757" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-3758">
                      <Input label="Apgar 1 Min" value={selectedMaternityFile.newbornEvaluation.apgar1} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    apgar1: e.target.value
                  }
                })} data-id="element-3759" />
                      <Input label="Apgar 5 Min" value={selectedMaternityFile.newbornEvaluation.apgar5} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    apgar5: e.target.value
                  }
                })} data-id="element-3760" />
                      <Input label="Apgar 10 Min" value={selectedMaternityFile.newbornEvaluation.apgar10} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    apgar10: e.target.value
                  }
                })} data-id="element-3761" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-3762">
                      <Input label="General Exam" value={selectedMaternityFile.newbornEvaluation.generalExam} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    generalExam: e.target.value
                  }
                })} data-id="element-3763" />
                      <Input label="Congenital Malformation" value={selectedMaternityFile.newbornEvaluation.congenitalMalformation} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    congenitalMalformation: e.target.value
                  }
                })} data-id="element-3764" />
                      <Input label="Describe" value={selectedMaternityFile.newbornEvaluation.congenitalMalformationDesc} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    congenitalMalformationDesc: e.target.value
                  }
                })} data-id="element-3765" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-3766">
                      <Input label="Infant ARVs" value={selectedMaternityFile.newbornEvaluation.infantARVs} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    infantARVs: e.target.value
                  }
                })} data-id="element-3767" />
                      <Input label="Infant Feeding" value={selectedMaternityFile.newbornEvaluation.infantFeeding} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    infantFeeding: e.target.value
                  }
                })} data-id="element-3768" />
                      <Input label="Resuscitation Methods" value={selectedMaternityFile.newbornEvaluation.resuscitationMethods} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    resuscitationMethods: e.target.value
                  }
                })} data-id="element-3769" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-3770">
                      <Input label="Crown Heel Length" value={selectedMaternityFile.newbornEvaluation.crownHeelLength} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    crownHeelLength: e.target.value
                  }
                })} data-id="element-3771" />
                      <Input label="Head Circumference" value={selectedMaternityFile.newbornEvaluation.headCircumference} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    headCircumference: e.target.value
                  }
                })} data-id="element-3772" />
                    </div>
                    <h4 className="font-medium text-gray-900 mt-6 mb-3 border-b pb-1" data-id="element-3773">
                      Physical Examination
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-3774">
                      <Input label="Skin Jaundice" value={selectedMaternityFile.newbornEvaluation.skinJaundice} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    skinJaundice: e.target.value
                  }
                })} data-id="element-3775" />
                      <Input label="Skin Other" value={selectedMaternityFile.newbornEvaluation.skinOther} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    skinOther: e.target.value
                  }
                })} data-id="element-3776" />
                      <Input label="Fontanelles" value={selectedMaternityFile.newbornEvaluation.fontanelles} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    fontanelles: e.target.value
                  }
                })} data-id="element-3777" />
                      <Input label="Eyes" value={selectedMaternityFile.newbornEvaluation.eyes} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    eyes: e.target.value
                  }
                })} data-id="element-3778" />
                      <Input label="Eyes Treatment" value={selectedMaternityFile.newbornEvaluation.eyesTreatment} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    eyesTreatment: e.target.value
                  }
                })} data-id="element-3779" />
                      <Input label="Mouth" value={selectedMaternityFile.newbornEvaluation.mouth} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    mouth: e.target.value
                  }
                })} data-id="element-3780" />
                      <Input label="Neck" value={selectedMaternityFile.newbornEvaluation.neck} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    neck: e.target.value
                  }
                })} data-id="element-3781" />
                      <Input label="Chest" value={selectedMaternityFile.newbornEvaluation.chest} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    chest: e.target.value
                  }
                })} data-id="element-3782" />
                      <Input label="Abdomen" value={selectedMaternityFile.newbornEvaluation.abdomen} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    abdomen: e.target.value
                  }
                })} data-id="element-3783" />
                      <Input label="Umbilicus" value={selectedMaternityFile.newbornEvaluation.umbilicus} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    umbilicus: e.target.value
                  }
                })} data-id="element-3784" />
                      <Input label="Genitalia" value={selectedMaternityFile.newbornEvaluation.genitalia} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    genitalia: e.target.value
                  }
                })} data-id="element-3785" />
                      <Input label="Urine" value={selectedMaternityFile.newbornEvaluation.urine} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    urine: e.target.value
                  }
                })} data-id="element-3786" />
                      <Input label="Anus" value={selectedMaternityFile.newbornEvaluation.anus} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    anus: e.target.value
                  }
                })} data-id="element-3787" />
                      <Input label="Meconium" value={selectedMaternityFile.newbornEvaluation.meconium} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    meconium: e.target.value
                  }
                })} data-id="element-3788" />
                      <Input label="Upper Limbs" value={selectedMaternityFile.newbornEvaluation.upperLimbs} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    upperLimbs: e.target.value
                  }
                })} data-id="element-3789" />
                      <Input label="Erb Paralysis" value={selectedMaternityFile.newbornEvaluation.erbParalysis} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    erbParalysis: e.target.value
                  }
                })} data-id="element-3790" />
                      <Input label="Lower Limbs" value={selectedMaternityFile.newbornEvaluation.lowerLimbs} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    lowerLimbs: e.target.value
                  }
                })} data-id="element-3791" />
                      <Input label="Ortolani Sign" value={selectedMaternityFile.newbornEvaluation.ortolaniSign} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    ortolaniSign: e.target.value
                  }
                })} data-id="element-3792" />
                      <Input label="Back" value={selectedMaternityFile.newbornEvaluation.back} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    back: e.target.value
                  }
                })} data-id="element-3793" />
                    </div>
                    <h4 className="font-medium text-gray-900 mt-6 mb-3 border-b pb-1" data-id="element-3794">
                      Reflexes
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-3795">
                      <Input label="Grasp" value={selectedMaternityFile.newbornEvaluation.reflexesGrasp} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    reflexesGrasp: e.target.value
                  }
                })} data-id="element-3796" />
                      <Input label="Moro (Startle)" value={selectedMaternityFile.newbornEvaluation.reflexesMoro} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    reflexesMoro: e.target.value
                  }
                })} data-id="element-3797" />
                      <Input label="Sucking" value={selectedMaternityFile.newbornEvaluation.reflexesSucking} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    reflexesSucking: e.target.value
                  }
                })} data-id="element-3798" />
                    </div>
                    <div className="mt-4" data-id="element-3799">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3800">
                        Newborn Progress Notes
                      </label>
                      <textarea className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-pink-500 focus:outline-none" rows={4} value={selectedMaternityFile.newbornEvaluation.progressNotes} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile,
                  newbornEvaluation: {
                    ...selectedMaternityFile.newbornEvaluation,
                    progressNotes: e.target.value
                  }
                })} data-id="element-3801" />
                    </div>
                  </Card>

                  <Card title="Post Delivery Observation Chart" data-id="element-3802">
                    <div className="overflow-x-auto" data-id="element-3803">
                      <table className="w-full text-sm text-left" data-id="element-3804">
                        <thead className="bg-gray-50" data-id="element-3805">
                          <tr data-id="element-3806">
                            <th className="p-2 border" colSpan={2} data-id="element-3807">
                              Time
                            </th>
                            <th className="p-2 border text-center" colSpan={8} data-id="element-3808">
                              Mother
                            </th>
                            <th className="p-2 border text-center" colSpan={7} data-id="element-3809">
                              Baby
                            </th>
                            <th className="p-2 border" rowSpan={2} data-id="element-3810">
                              Sign
                            </th>
                            <th className="p-2 border" rowSpan={2} data-id="element-3811"></th>
                          </tr>
                          <tr data-id="element-3812">
                            <th className="p-2 border" data-id="element-3813">Date</th>
                            <th className="p-2 border" data-id="element-3814">Time</th>
                            <th className="p-2 border" data-id="element-3815">BP</th>
                            <th className="p-2 border" data-id="element-3816">Pulse</th>
                            <th className="p-2 border" data-id="element-3817">Resps</th>
                            <th className="p-2 border" data-id="element-3818">Temp</th>
                            <th className="p-2 border" data-id="element-3819">PV Bleed</th>
                            <th className="p-2 border" data-id="element-3820">Uterus</th>
                            <th className="p-2 border" data-id="element-3821">Bladder</th>
                            <th className="p-2 border" data-id="element-3822">Pallor</th>
                            <th className="p-2 border" data-id="element-3823">Temp</th>
                            <th className="p-2 border" data-id="element-3824">Resps</th>
                            <th className="p-2 border" data-id="element-3825">Cord</th>
                            <th className="p-2 border" data-id="element-3826">Skin</th>
                            <th className="p-2 border" data-id="element-3827">Feed BF/AF</th>
                            <th className="p-2 border" data-id="element-3828">Bowel</th>
                            <th className="p-2 border" data-id="element-3829">Urine</th>
                          </tr>
                        </thead>
                        <tbody data-id="element-3830">
                          {selectedMaternityFile.postDeliveryObservations.map((obs, index) => <tr key={obs.id} data-id="element-3831">
                                <td className="p-1 border" data-id="element-3832">
                                  <input className="w-full p-1 border rounded" value={obs.date} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].date = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-3833" />
                                </td>
                                <td className="p-1 border" data-id="element-3834">
                                  <input className="w-full p-1 border rounded" value={obs.time} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].time = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-3835" />
                                </td>
                                <td className="p-1 border" data-id="element-3836">
                                  <input className="w-full p-1 border rounded" value={obs.bp} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].bp = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-3837" />
                                </td>
                                <td className="p-1 border" data-id="element-3838">
                                  <input className="w-full p-1 border rounded" value={obs.pulse} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].pulse = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-3839" />
                                </td>
                                <td className="p-1 border" data-id="element-3840">
                                  <input className="w-full p-1 border rounded" value={obs.resps} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].resps = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-3841" />
                                </td>
                                <td className="p-1 border" data-id="element-3842">
                                  <input className="w-full p-1 border rounded" value={obs.temp} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].temp = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-3843" />
                                </td>
                                <td className="p-1 border" data-id="element-3844">
                                  <input className="w-full p-1 border rounded" value={obs.pvBleeding} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].pvBleeding = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-3845" />
                                </td>
                                <td className="p-1 border" data-id="element-3846">
                                  <input className="w-full p-1 border rounded" value={obs.uterus} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].uterus = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-3847" />
                                </td>
                                <td className="p-1 border" data-id="element-3848">
                                  <input className="w-full p-1 border rounded" value={obs.bladder} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].bladder = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-3849" />
                                </td>
                                <td className="p-1 border" data-id="element-3850">
                                  <input className="w-full p-1 border rounded" value={obs.pallor} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].pallor = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-3851" />
                                </td>
                                <td className="p-1 border" data-id="element-3852">
                                  <input className="w-full p-1 border rounded" value={obs.babyTemp} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].babyTemp = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-3853" />
                                </td>
                                <td className="p-1 border" data-id="element-3854">
                                  <input className="w-full p-1 border rounded" value={obs.babyResps} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].babyResps = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-3855" />
                                </td>
                                <td className="p-1 border" data-id="element-3856">
                                  <input className="w-full p-1 border rounded" value={obs.cord} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].cord = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-3857" />
                                </td>
                                <td className="p-1 border" data-id="element-3858">
                                  <input className="w-full p-1 border rounded" value={obs.skinColour} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].skinColour = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-3859" />
                                </td>
                                <td className="p-1 border" data-id="element-3860">
                                  <input className="w-full p-1 border rounded" value={obs.feedingBF} placeholder="BF" onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].feedingBF = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-3861" />
                                  <input className="w-full p-1 border rounded mt-1" value={obs.feedingAF} placeholder="AF" onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].feedingAF = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-3862" />
                                </td>
                                <td className="p-1 border" data-id="element-3863">
                                  <input className="w-full p-1 border rounded" value={obs.eliminationBowel} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].eliminationBowel = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-3864" />
                                </td>
                                <td className="p-1 border" data-id="element-3865">
                                  <input className="w-full p-1 border rounded" value={obs.eliminationUrine} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].eliminationUrine = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-3866" />
                                </td>
                                <td className="p-1 border" data-id="element-3867">
                                  <input className="w-full p-1 border rounded" value={obs.sign} onChange={e => {
                          const newObs = [...selectedMaternityFile.postDeliveryObservations];
                          newObs[index].sign = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} data-id="element-3868" />
                                </td>
                                <td className="p-1 border text-center" data-id="element-3869">
                                  <button onClick={() => {
                          const newObs = selectedMaternityFile.postDeliveryObservations.filter((_, i) => i !== index);
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            postDeliveryObservations: newObs
                          });
                        }} className="text-red-500 hover:text-red-700" data-id="element-3870">
                                    <Trash2 className="w-4 h-4" data-id="element-3871" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => setSelectedMaternityFile({
                ...selectedMaternityFile,
                postDeliveryObservations: [...selectedMaternityFile.postDeliveryObservations, {
                  id: Date.now().toString(),
                  date: '',
                  time: '',
                  bp: '',
                  pulse: '',
                  resps: '',
                  temp: '',
                  pvBleeding: '',
                  uterus: '',
                  bladder: '',
                  pallor: '',
                  babyTemp: '',
                  babyResps: '',
                  cord: '',
                  skinColour: '',
                  feedingBF: '',
                  feedingAF: '',
                  eliminationBowel: '',
                  eliminationUrine: '',
                  sign: ''
                }]
              })} data-id="element-3872">
                      + Add Observation
                    </Button>
                  </Card>

                  <Card title="Drug Chart" data-id="element-3873">
                    <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-3874">
                      <Input label="Ward" value={selectedMaternityFile?.drugChart?.ward || ''} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile!,
                  drugChart: {
                    ...(selectedMaternityFile?.drugChart || {
                      ward: '',
                      patientNo: '',
                      supplyDates: ['', '', ''],
                      rows: [],
                      signedBy: '',
                      signedDate: ''
                    }),
                    ward: e.target.value
                  }
                })} data-id="element-3875" />
                      <Input label="Patient No." value={selectedMaternityFile?.drugChart?.patientNo || ''} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile!,
                  drugChart: {
                    ...(selectedMaternityFile?.drugChart || {
                      ward: '',
                      patientNo: '',
                      supplyDates: ['', '', ''],
                      rows: [],
                      signedBy: '',
                      signedDate: ''
                    }),
                    patientNo: e.target.value
                  }
                })} data-id="element-3876" />
                    </div>
                    <div className="overflow-x-auto" data-id="element-3877">
                      <table className="w-full text-xs text-center border-collapse border border-gray-300 min-w-[1200px]" data-id="element-3878">
                        <thead className="bg-gray-50" data-id="element-3879">
                          <tr data-id="element-3880">
                            <th className="p-1 border border-gray-300 w-24" rowSpan={3} data-id="element-3881">
                              Date
                            </th>
                            <th className="p-1 border border-gray-300 w-32" rowSpan={3} data-id="element-3882">
                              Drug
                            </th>
                            <th className="p-1 border border-gray-300 w-20" rowSpan={3} data-id="element-3883">
                              Dosage
                            </th>
                            <th className="p-1 border border-gray-300 w-20" rowSpan={3} data-id="element-3884">
                              Route of Admin
                            </th>
                            <th className="p-1 border border-gray-300 w-24" rowSpan={3} data-id="element-3885">
                              Ordered By
                            </th>
                            <th className="p-1 border border-gray-300" colSpan={21} data-id="element-3886">
                              SUPPLIED BY (DATE)
                            </th>
                            <th className="p-1 border border-gray-300 w-24" rowSpan={3} data-id="element-3887">
                              Supplied By
                            </th>
                            <th className="p-1 border border-gray-300 w-20" rowSpan={3} data-id="element-3888">
                              Quantity
                            </th>
                            <th className="p-1 border border-gray-300 w-24" rowSpan={3} data-id="element-3889">
                              Date
                            </th>
                          </tr>
                          <tr data-id="element-3890">
                            {[0, 1, 2].map(colIdx => <th key={colIdx} className="p-1 border border-gray-300" colSpan={7} data-id="element-3891">
                                <input type="date" className="w-full border-gray-300 rounded-sm text-xs" value={selectedMaternityFile?.drugChart?.supplyDates?.[colIdx] || ''} onChange={e => {
                          const newDates = [...(selectedMaternityFile?.drugChart?.supplyDates || ['', '', ''])] as [string, string, string];
                          newDates[colIdx] = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...(selectedMaternityFile!.drugChart || {
                                ward: '',
                                patientNo: '',
                                supplyDates: ['', '', ''],
                                rows: [],
                                signedBy: '',
                                signedDate: ''
                              }),
                              supplyDates: newDates
                            }
                          });
                        }} data-id="element-3892" />
                              </th>)}
                          </tr>
                          <tr data-id="element-3893">
                            {[0, 1, 2].map(colIdx => <Fragment key={`times-${colIdx}`} data-id="element-3894">
                                {['6', '10', '12', '14', '18', '22', '24'].map(time => <th key={`${colIdx}-${time}`} className="p-1 border border-gray-300 w-8 font-normal" data-id="element-3895">
                                      {time}
                                    </th>)}
                              </Fragment>)}
                          </tr>
                        </thead>
                        <tbody data-id="element-3896">
                          {(selectedMaternityFile?.drugChart?.rows || []).map((row, rowIndex) => <tr key={row.id} data-id="element-3897">
                                <td className="p-0 border border-gray-300" data-id="element-3898">
                                  <input type="date" className="w-full border-0 text-xs p-1" value={row.date} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].date = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-3899" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-3900">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.drug} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].drug = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-3901" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-3902">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.dosage} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].dosage = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-3903" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-3904">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.routeOfAdmin} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].routeOfAdmin = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-3905" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-3906">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.orderedBy} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].orderedBy = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-3907" />
                                </td>
                                {[0, 1, 2].map(colIdx => <Fragment key={`admin-${colIdx}`} data-id="element-3908">
                                    {['6', '10', '12', '14', '18', '22', '24'].map(time => {
                          const key = `${colIdx}-${time}`;
                          return <td key={key} className="p-0 border border-gray-300" data-id="element-3909">
                                          <input type="text" className="w-full border-0 text-xs p-1 text-center" value={row.administration?.[key] || ''} onChange={e => {
                              const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                              newRows[rowIndex].administration = {
                                ...newRows[rowIndex].administration,
                                [key]: e.target.value
                              };
                              setSelectedMaternityFile({
                                ...selectedMaternityFile!,
                                drugChart: {
                                  ...selectedMaternityFile!.drugChart!,
                                  rows: newRows
                                }
                              });
                            }} data-id="element-3910" />
                                        </td>;
                        })}
                                  </Fragment>)}
                                <td className="p-0 border border-gray-300" data-id="element-3911">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.suppliedBy} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].suppliedBy = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-3912" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-3913">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.quantity} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].quantity = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-3914" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-3915">
                                  <input type="date" className="w-full border-0 text-xs p-1" value={row.suppliedDate} onChange={e => {
                          const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                          newRows[rowIndex].suppliedDate = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} data-id="element-3916" />
                                </td>
                                <td className="p-1 border border-gray-300" data-id="element-3917">
                                  <button type="button" onClick={() => {
                          const newRows = selectedMaternityFile!.drugChart!.rows.filter((_, i) => i !== rowIndex);
                          setSelectedMaternityFile({
                            ...selectedMaternityFile!,
                            drugChart: {
                              ...selectedMaternityFile!.drugChart!,
                              rows: newRows
                            }
                          });
                        }} className="text-red-500 hover:text-red-700" data-id="element-3918">
                                    <Trash2 className="w-4 h-4" data-id="element-3919" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => {
                const currentChart = selectedMaternityFile?.drugChart || {
                  ward: '',
                  patientNo: '',
                  supplyDates: ['', '', ''],
                  rows: [],
                  signedBy: '',
                  signedDate: ''
                };
                setSelectedMaternityFile({
                  ...selectedMaternityFile!,
                  drugChart: {
                    ...currentChart,
                    rows: [...currentChart.rows, {
                      id: `drug-${Date.now()}`,
                      date: '',
                      drug: '',
                      dosage: '',
                      routeOfAdmin: '',
                      orderedBy: '',
                      administration: {},
                      suppliedBy: '',
                      quantity: '',
                      suppliedDate: ''
                    }]
                  }
                });
              }} data-id="element-3920">
                      + Add Drug Row
                    </Button>
                    <div className="grid grid-cols-2 gap-4 mt-6" data-id="element-3921">
                      <Input label="Date" type="date" value={selectedMaternityFile?.drugChart?.signedDate || ''} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile!,
                  drugChart: {
                    ...(selectedMaternityFile?.drugChart || {
                      ward: '',
                      patientNo: '',
                      supplyDates: ['', '', ''],
                      rows: [],
                      signedBy: '',
                      signedDate: ''
                    }),
                    signedDate: e.target.value
                  }
                })} data-id="element-3922" />
                      <Input label="Signature" value={selectedMaternityFile?.drugChart?.signedBy || ''} onChange={e => setSelectedMaternityFile({
                  ...selectedMaternityFile!,
                  drugChart: {
                    ...(selectedMaternityFile?.drugChart || {
                      ward: '',
                      patientNo: '',
                      supplyDates: ['', '', ''],
                      rows: [],
                      signedBy: '',
                      signedDate: ''
                    }),
                    signedBy: e.target.value
                  }
                })} data-id="element-3923" />
                    </div>
                  </Card>

                  <Card title="Puerperium Notes" data-id="element-3924">
                    <div className="overflow-x-auto" data-id="element-3925">
                      <table className="w-full text-sm text-left" data-id="element-3926">
                        <thead className="bg-gray-50" data-id="element-3927">
                          <tr data-id="element-3928">
                            <th className="p-2 border w-32" data-id="element-3929">Date</th>
                            <th className="p-2 border" data-id="element-3930">Notes</th>
                            <th className="p-2 border w-48" data-id="element-3931">Signature</th>
                            <th className="p-2 border w-12" data-id="element-3932"></th>
                          </tr>
                        </thead>
                        <tbody data-id="element-3933">
                          {selectedMaternityFile.puerperiumNotes.map((note, index) => <tr key={note.id} data-id="element-3934">
                                <td className="p-1 border" data-id="element-3935">
                                  <Input type="date" value={note.date} onChange={e => {
                          const newNotes = [...selectedMaternityFile.puerperiumNotes];
                          newNotes[index].date = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            puerperiumNotes: newNotes
                          });
                        }} data-id="element-3936" />
                                </td>
                                <td className="p-1 border" data-id="element-3937">
                                  <textarea className="w-full p-1 border rounded" rows={2} value={note.notes} onChange={e => {
                          const newNotes = [...selectedMaternityFile.puerperiumNotes];
                          newNotes[index].notes = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            puerperiumNotes: newNotes
                          });
                        }} data-id="element-3938" />
                                </td>
                                <td className="p-1 border" data-id="element-3939">
                                  <Input value={note.signature} onChange={e => {
                          const newNotes = [...selectedMaternityFile.puerperiumNotes];
                          newNotes[index].signature = e.target.value;
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            puerperiumNotes: newNotes
                          });
                        }} data-id="element-3940" />
                                </td>
                                <td className="p-1 border text-center" data-id="element-3941">
                                  <button onClick={() => {
                          const newNotes = selectedMaternityFile.puerperiumNotes.filter((_, i) => i !== index);
                          setSelectedMaternityFile({
                            ...selectedMaternityFile,
                            puerperiumNotes: newNotes
                          });
                        }} className="text-red-500 hover:text-red-700" data-id="element-3942">
                                    <Trash2 className="w-4 h-4" data-id="element-3943" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => setSelectedMaternityFile({
                ...selectedMaternityFile,
                puerperiumNotes: [...selectedMaternityFile.puerperiumNotes, {
                  id: Date.now().toString(),
                  date: '',
                  notes: '',
                  signature: ''
                }]
              })} data-id="element-3944">
                      + Add Note
                    </Button>
                  </Card>

                  <Card title="Discharge Slip" data-id="element-3945">
                    {(() => {
                const defaultSlip = {
                  smNo: '',
                  gravida: '',
                  patientName: '',
                  party: '',
                  timeOfDischarge: '',
                  age: '',
                  motherDob: '',
                  modeOfDelivery: '',
                  perineum: '',
                  comments: '',
                  pca: '',
                  infantTob: '',
                  apgarScore: '',
                  infantSex: '',
                  dateOfDischarge: '',
                  mother: {
                    bp: '',
                    pallor: '',
                    breast: '',
                    breastfeeding: '',
                    uterus: '',
                    perineum: '',
                    lochia: '',
                    bowels: '',
                    urine: '',
                    familyPlanning: '',
                    dangerSigns: '',
                    treatment: '',
                    vitaminA: ''
                  },
                  infant: {
                    birthWeight: '',
                    dischargeWeight: '',
                    pallor: '',
                    jaundice: '',
                    passedMeconiumUrine: '',
                    feeding: '',
                    feedingOption: '',
                    breastFeeding: '',
                    bcg: '',
                    opv: '',
                    birthRecord: '',
                    gc: '',
                    treatment: ''
                  },
                  sixDaysPostnatalDate: '',
                  sixWeeksPostnatalDate: '',
                  nameInFull: ''
                };
                const slip = selectedMaternityFile.dischargeSlip || defaultSlip;
                const updateSlip = (patch: Partial<typeof defaultSlip>) => {
                  setSelectedMaternityFile({
                    ...selectedMaternityFile,
                    dischargeSlip: {
                      ...slip,
                      ...patch
                    }
                  });
                };
                const updateMother = (patch: Partial<typeof defaultSlip.mother>) => {
                  updateSlip({
                    mother: {
                      ...slip.mother,
                      ...patch
                    }
                  });
                };
                const updateInfant = (patch: Partial<typeof defaultSlip.infant>) => {
                  updateSlip({
                    infant: {
                      ...slip.infant,
                      ...patch
                    }
                  });
                };
                return <div className="space-y-6" data-id="element-3946">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3947">
                            <Input label="SM No" value={slip.smNo} onChange={e => updateSlip({
                      smNo: e.target.value
                    })} data-id="element-3948" />
                            <Input label="Gravida" value={slip.gravida} onChange={e => updateSlip({
                      gravida: e.target.value
                    })} data-id="element-3949" />
                            <Input label="Patient's Name" value={slip.patientName} onChange={e => updateSlip({
                      patientName: e.target.value
                    })} data-id="element-3950" />
                            <Input label="Party" value={slip.party} onChange={e => updateSlip({
                      party: e.target.value
                    })} data-id="element-3951" />
                            <Input label="Time of Discharge" type="time" value={slip.timeOfDischarge} onChange={e => updateSlip({
                      timeOfDischarge: e.target.value
                    })} data-id="element-3952" />
                            <Input label="Age" value={slip.age} onChange={e => updateSlip({
                      age: e.target.value
                    })} data-id="element-3953" />
                          </div>

                          <div data-id="element-3954">
                            <h4 className="font-semibold text-sm uppercase mb-2 border-b pb-1" data-id="element-3955">
                              Labour and Delivery Summary
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3956">
                              <div data-id="element-3957">
                                <h5 className="font-semibold mb-2 text-pink-600" data-id="element-3958">
                                  Mother
                                </h5>
                                <div className="space-y-3" data-id="element-3959">
                                  <Input label="D.O.B" type="date" value={slip.motherDob} onChange={e => updateSlip({
                            motherDob: e.target.value
                          })} data-id="element-3960" />
                                  <Input label="M.O.D (Mode of Delivery)" value={slip.modeOfDelivery} onChange={e => updateSlip({
                            modeOfDelivery: e.target.value
                          })} data-id="element-3961" />
                                  <Input label="Perineum" value={slip.perineum} onChange={e => updateSlip({
                            perineum: e.target.value
                          })} data-id="element-3962" />
                                  <div data-id="element-3963">
                                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3964">
                                      Comments
                                    </label>
                                    <textarea className="w-full p-2 border rounded text-sm" rows={2} value={slip.comments} onChange={e => updateSlip({
                              comments: e.target.value
                            })} data-id="element-3965" />
                                  </div>
                                  <Input label="P.C.A" value={slip.pca} onChange={e => updateSlip({
                            pca: e.target.value
                          })} data-id="element-3966" />
                                </div>
                              </div>
                              <div data-id="element-3967">
                                <h5 className="font-semibold mb-2 text-pink-600" data-id="element-3968">
                                  Infant
                                </h5>
                                <div className="space-y-3" data-id="element-3969">
                                  <Input label="T.O.B (Time of Birth)" value={slip.infantTob} onChange={e => updateSlip({
                            infantTob: e.target.value
                          })} data-id="element-3970" />
                                  <Input label="A/S (Apgar Score)" value={slip.apgarScore} onChange={e => updateSlip({
                            apgarScore: e.target.value
                          })} data-id="element-3971" />
                                  <Input label="Sex" value={slip.infantSex} onChange={e => updateSlip({
                            infantSex: e.target.value
                          })} data-id="element-3972" />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div data-id="element-3973">
                            <div className="flex justify-between items-end border-b pb-1 mb-2" data-id="element-3974">
                              <h4 className="font-semibold text-sm uppercase" data-id="element-3975">
                                Discharge Summary
                              </h4>
                              <div className="w-64" data-id="element-3976">
                                <Input label="Date of Discharge" type="date" value={slip.dateOfDischarge} onChange={e => updateSlip({
                          dateOfDischarge: e.target.value
                        })} data-id="element-3977" />
                              </div>
                            </div>

                            <div className="bg-pink-50 p-3 rounded mb-4" data-id="element-3978">
                              <h5 className="font-semibold mb-3 uppercase text-sm" data-id="element-3979">
                                Mother
                              </h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-3980">
                                <Input label="BP" value={slip.mother.bp} onChange={e => updateMother({
                          bp: e.target.value
                        })} data-id="element-3981" />
                                <Input label="Pallor" value={slip.mother.pallor} onChange={e => updateMother({
                          pallor: e.target.value
                        })} data-id="element-3982" />
                                <Input label="Breast" value={slip.mother.breast} onChange={e => updateMother({
                          breast: e.target.value
                        })} data-id="element-3983" />
                                <div data-id="element-3984">
                                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3985">
                                    Breastfeeding
                                  </label>
                                  <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" value={slip.mother.breastfeeding} onChange={e => updateMother({
                            breastfeeding: e.target.value
                          })} data-id="element-3986">
                                    <option value="" data-id="element-3987">—</option>
                                    <option value="Yes" data-id="element-3988">Yes</option>
                                    <option value="No" data-id="element-3989">No</option>
                                  </select>
                                </div>
                                <Input label="Uterus" value={slip.mother.uterus} onChange={e => updateMother({
                          uterus: e.target.value
                        })} data-id="element-3990" />
                                <Input label="Perineum" value={slip.mother.perineum} onChange={e => updateMother({
                          perineum: e.target.value
                        })} data-id="element-3991" />
                                <Input label="Lochia" value={slip.mother.lochia} onChange={e => updateMother({
                          lochia: e.target.value
                        })} data-id="element-3992" />
                                <Input label="Bowels" value={slip.mother.bowels} onChange={e => updateMother({
                          bowels: e.target.value
                        })} data-id="element-3993" />
                                <Input label="Urine" value={slip.mother.urine} onChange={e => updateMother({
                          urine: e.target.value
                        })} data-id="element-3994" />
                                <Input label="Family Planning" value={slip.mother.familyPlanning} onChange={e => updateMother({
                          familyPlanning: e.target.value
                        })} data-id="element-3995" />
                                <div className="md:col-span-2" data-id="element-3996">
                                  <Input label="Danger Signs" value={slip.mother.dangerSigns} onChange={e => updateMother({
                            dangerSigns: e.target.value
                          })} data-id="element-3997" />
                                </div>
                                <div className="md:col-span-2" data-id="element-3998">
                                  <Input label="Treatment" value={slip.mother.treatment} onChange={e => updateMother({
                            treatment: e.target.value
                          })} data-id="element-3999" />
                                </div>
                                <Input label="Vitamin A" value={slip.mother.vitaminA} onChange={e => updateMother({
                          vitaminA: e.target.value
                        })} data-id="element-4000" />
                              </div>
                            </div>

                            <div className="bg-blue-50 p-3 rounded mb-4" data-id="element-4001">
                              <h5 className="font-semibold mb-3 uppercase text-sm" data-id="element-4002">
                                Infant
                              </h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4003">
                                <Input label="BWT (Birth Weight)" value={slip.infant.birthWeight} onChange={e => updateInfant({
                          birthWeight: e.target.value
                        })} data-id="element-4004" />
                                <Input label="Discharge Weight" value={slip.infant.dischargeWeight} onChange={e => updateInfant({
                          dischargeWeight: e.target.value
                        })} data-id="element-4005" />
                                <Input label="Pallor" value={slip.infant.pallor} onChange={e => updateInfant({
                          pallor: e.target.value
                        })} data-id="element-4006" />
                                <Input label="Jaundice" value={slip.infant.jaundice} onChange={e => updateInfant({
                          jaundice: e.target.value
                        })} data-id="element-4007" />
                                <div className="md:col-span-2" data-id="element-4008">
                                  <Input label="Passed Meconium / Urine" value={slip.infant.passedMeconiumUrine} onChange={e => updateInfant({
                            passedMeconiumUrine: e.target.value
                          })} data-id="element-4009" />
                                </div>
                                <Input label="Feeding" value={slip.infant.feeding} onChange={e => updateInfant({
                          feeding: e.target.value
                        })} data-id="element-4010" />
                                <Input label="Feeding Option" value={slip.infant.feedingOption} onChange={e => updateInfant({
                          feedingOption: e.target.value
                        })} data-id="element-4011" />
                                <Input label="Breast Feeding" value={slip.infant.breastFeeding} onChange={e => updateInfant({
                          breastFeeding: e.target.value
                        })} data-id="element-4012" />
                                <Input label="BCG" value={slip.infant.bcg} onChange={e => updateInfant({
                          bcg: e.target.value
                        })} data-id="element-4013" />
                                <Input label="OPV" value={slip.infant.opv} onChange={e => updateInfant({
                          opv: e.target.value
                        })} data-id="element-4014" />
                                <Input label="Birth Record" value={slip.infant.birthRecord} onChange={e => updateInfant({
                          birthRecord: e.target.value
                        })} data-id="element-4015" />
                                <Input label="G/C" value={slip.infant.gc} onChange={e => updateInfant({
                          gc: e.target.value
                        })} data-id="element-4016" />
                                <div className="md:col-span-2" data-id="element-4017">
                                  <Input label="Treatment" value={slip.infant.treatment} onChange={e => updateInfant({
                            treatment: e.target.value
                          })} data-id="element-4018" />
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4019">
                              <Input label="6 days Postnatal Date" type="date" value={slip.sixDaysPostnatalDate} onChange={e => updateSlip({
                        sixDaysPostnatalDate: e.target.value
                      })} data-id="element-4020" />
                              <Input label="6 Weeks Postnatal Date" type="date" value={slip.sixWeeksPostnatalDate} onChange={e => updateSlip({
                        sixWeeksPostnatalDate: e.target.value
                      })} data-id="element-4021" />
                              <div className="md:col-span-2" data-id="element-4022">
                                <Input label="Name in Full" value={slip.nameInFull} onChange={e => updateSlip({
                          nameInFull: e.target.value
                        })} data-id="element-4023" />
                              </div>
                            </div>
                          </div>
                        </div>;
              })()}
                  </Card>

                  {(() => {
              const defaultVisit = {
                date: '',
                mother: {
                  generalWellBeing: '',
                  temp: '',
                  pulse: '',
                  bp: '',
                  urine: '',
                  head: '',
                  eyes: '',
                  ears: '',
                  mouth: '',
                  neck: '',
                  breasts: '',
                  ampills: '',
                  abdomenUterus: '',
                  groin: '',
                  perineum: '',
                  legs: '',
                  anus: '',
                  lochia: '',
                  cervixPerSpeculum: '',
                  familyPlanning: '',
                  treatmentOrAdvise: ''
                },
                infant: {
                  name: '',
                  weight: '',
                  date: '',
                  headCircumference: '',
                  length: '',
                  temp: '',
                  head: '',
                  eyes: '',
                  ears: '',
                  mouth: '',
                  neck: '',
                  chest: '',
                  abdomen: '',
                  cord: '',
                  genitaliaRectum: '',
                  extremities: '',
                  back: '',
                  neurologicExam: '',
                  feeding: '',
                  additionalNotes: ''
                }
              };
              const motherFieldOrder: Array<[keyof typeof defaultVisit.mother, string]> = [['generalWellBeing', 'General Well Being'], ['temp', 'Temp'], ['pulse', 'Pulse'], ['bp', 'BP'], ['urine', 'Urine'], ['head', 'Head'], ['eyes', 'Eyes'], ['ears', 'Ears'], ['mouth', 'Mouth'], ['neck', 'Neck'], ['breasts', 'Breasts'], ['ampills', 'Ampills'], ['abdomenUterus', 'Abdomen (Uterus)'], ['groin', 'Groin'], ['perineum', 'Perineum'], ['legs', 'Legs'], ['anus', 'Anus'], ['lochia', 'Lochia']];
              const infantFieldOrder: Array<[keyof typeof defaultVisit.infant, string]> = [['headCircumference', 'Head Circumference'], ['length', 'Length'], ['temp', 'Temp'], ['head', 'Head'], ['eyes', 'Eyes'], ['ears', 'Ears'], ['mouth', 'Mouth'], ['neck', 'Neck'], ['chest', 'Chest'], ['abdomen', 'Abdomen'], ['cord', 'Cord'], ['genitaliaRectum', 'Genitalia & Rectum'], ['extremities', 'Extremities'], ['back', 'Back'], ['neurologicExam', 'Neurologic Exam'], ['feeding', 'Feeding']];
              const renderVisitForm = (visitKey: 'postNatalOneWeek' | 'postNatalSixWeeks') => {
                const visit = (selectedMaternityFile as any)[visitKey] || defaultVisit;
                const updateVisit = (patch: Partial<typeof defaultVisit>) => {
                  setSelectedMaternityFile({
                    ...selectedMaternityFile,
                    [visitKey]: {
                      ...visit,
                      ...patch
                    }
                  });
                };
                const updateMother = (patch: Partial<typeof defaultVisit.mother>) => {
                  updateVisit({
                    mother: {
                      ...visit.mother,
                      ...patch
                    }
                  });
                };
                const updateInfant = (patch: Partial<typeof defaultVisit.infant>) => {
                  updateVisit({
                    infant: {
                      ...visit.infant,
                      ...patch
                    }
                  });
                };
                return <div className="space-y-6" data-id="element-4024">
                          <Input label="Date" type="date" value={visit.date} onChange={e => updateVisit({
                    date: e.target.value
                  })} data-id="element-4025" />

                          <div className="bg-pink-50 p-3 rounded" data-id="element-4026">
                            <h5 className="font-semibold mb-3 uppercase text-sm" data-id="element-4027">
                              Mother
                            </h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4028">
                              {motherFieldOrder.map(([key, label]) => <Input key={key} label={label} value={visit.mother[key]} onChange={e => updateMother({
                        [key]: e.target.value
                      } as any)} data-id="element-4029" />)}
                            </div>

                            <div className="mt-4 space-y-3" data-id="element-4030">
                              <h6 className="font-semibold text-sm uppercase" data-id="element-4031">
                                Vaginal Examination
                              </h6>
                              <div data-id="element-4032">
                                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4033">
                                  Cervix per speculum
                                </label>
                                <textarea className="w-full p-2 border rounded text-sm" rows={2} value={visit.mother.cervixPerSpeculum} onChange={e => updateMother({
                          cervixPerSpeculum: e.target.value
                        })} data-id="element-4034" />
                              </div>
                              <div data-id="element-4035">
                                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4036">
                                  Family Planning
                                </label>
                                <textarea className="w-full p-2 border rounded text-sm" rows={2} value={visit.mother.familyPlanning} onChange={e => updateMother({
                          familyPlanning: e.target.value
                        })} data-id="element-4037" />
                              </div>
                              <div data-id="element-4038">
                                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4039">
                                  Treatment or Advise
                                </label>
                                <textarea className="w-full p-2 border rounded text-sm" rows={3} value={visit.mother.treatmentOrAdvise} onChange={e => updateMother({
                          treatmentOrAdvise: e.target.value
                        })} data-id="element-4040" />
                              </div>
                            </div>
                          </div>

                          <div className="bg-blue-50 p-3 rounded" data-id="element-4041">
                            <h5 className="font-semibold mb-3 uppercase text-sm" data-id="element-4042">
                              Infant Examination Form
                            </h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3" data-id="element-4043">
                              <Input label="Infant Name" value={visit.infant.name} onChange={e => updateInfant({
                        name: e.target.value
                      })} data-id="element-4044" />
                              <Input label="Weight" value={visit.infant.weight} onChange={e => updateInfant({
                        weight: e.target.value
                      })} data-id="element-4045" />
                              <Input label="Date" type="date" value={visit.infant.date} onChange={e => updateInfant({
                        date: e.target.value
                      })} data-id="element-4046" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4047">
                              {infantFieldOrder.map(([key, label]) => <Input key={key} label={label} value={visit.infant[key]} onChange={e => updateInfant({
                        [key]: e.target.value
                      } as any)} data-id="element-4048" />)}
                            </div>
                            <div className="mt-3" data-id="element-4049">
                              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4050">
                                Additional Notes
                              </label>
                              <textarea className="w-full p-2 border rounded text-sm" rows={3} value={visit.infant.additionalNotes} onChange={e => updateInfant({
                        additionalNotes: e.target.value
                      })} data-id="element-4051" />
                            </div>
                          </div>
                        </div>;
              };
              return <>
                        <Card title="Postnatal Visit — One Week" data-id="element-4052">
                          {renderVisitForm('postNatalOneWeek')}
                        </Card>
                        <Card title="Postnatal Visit — Six Weeks" data-id="element-4053">
                          {renderVisitForm('postNatalSixWeeks')}
                        </Card>
                      </>;
            })()}
                </div>
              </MaternityErrorBoundary> : <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-500 h-full flex flex-col items-center justify-center" data-id="element-4054">
                <Baby className="w-16 h-16 text-gray-300 mb-4" data-id="element-4055" />
                <p className="text-lg font-medium text-gray-900" data-id="element-4056">
                  Select a maternity file
                </p>
                <p data-id="element-4057">
                  Choose a patient from the list to view and update their
                  maternity records.
                </p>
              </div>}
          </div>
        </div>}
    </div>;
};